import { gql } from '@apollo/client';

export const GET_CUSTOMER_DATA = gql`
  query getCustomer {
    customer {
      id
      email
      firstname
      lastname
      date_of_birth
      gender
      addresses {
        id
        firstname
        lastname
        street
        city
        region {
          region
          region_id
        }
        postcode
        country_code
        country_id
        telephone
        company
        default_shipping
        default_billing
      }
    }
  }
`;

export const GET_CUSTOMER_ORDERS = gql`
  query getCustomerOrders($pageSize: Int!, $currentPage: Int!) {
    customer {
      orders(pageSize: $pageSize, currentPage: $currentPage) {
        items {
          id
          number
          order_date
          status
          total {
            grand_total {
              value
              currency
            }
            base_grand_total {
              value
              currency
            }
            subtotal {
              value
              currency
            }
            total_tax {
              value
              currency
            }
            total_shipping {
              value
              currency
            }
          }
          items {
            id
            product_name
            product_sku
            quantity_ordered
            product_sale_price {
              value
              currency
            }
            product_url_key
          }
          shipping_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
          }
          billing_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
          }
          payment_methods {
            name
            type
          }
          shipments {
            id
            tracking {
              title
              number
            }
          }
        }
        page_info {
          page_size
          current_page
          total_pages
        }
        total_count
      }
    }
  }
`;

export const GET_CUSTOMER_DOWNLOADABLE_PRODUCTS = gql`
  query getCustomerDownloadableProducts {
    customerDownloadableProducts {
      items {
        date
        download_url
        order_increment_id
        remaining_downloads
        status
      }
    }
  }
`;

export const GET_CUSTOMER_WISHLIST = gql`
  query getCustomerWishlist($currentPage: Int = 1, $pageSize: Int = 100) {
    customer {
      wishlists {
        id
        items_count
        sharing_code
        updated_at
        items_v2(currentPage: $currentPage, pageSize: $pageSize) {
          items {
            id
            quantity
            description
            added_at
            product {
              id
              name
              sku
              url_key
              __typename
              image {
                url
                label
              }
              price_range {
                minimum_price {
                  regular_price {
                    value
                    currency
                  }
                  final_price {
                    value
                    currency
                  }
                }
              }
            }
            ... on ConfigurableWishlistItem {
              configurable_options {
                id
                option_label
                value_id
                value_label
              }
            }
          }
          page_info {
            page_size
            current_page
            total_pages
          }
        }
      }
    }
  }
`;

export const UPDATE_CUSTOMER_PROFILE = gql`
  mutation updateCustomer($input: CustomerInput!) {
    updateCustomer(input: $input) {
      customer {
        id
        email
        firstname
        lastname
        date_of_birth
        gender
      }
    }
  }
`;

export const CHANGE_CUSTOMER_PASSWORD = gql`
  mutation changeCustomerPassword($currentPassword: String!, $newPassword: String!) {
    changeCustomerPassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      id
      email
    }
  }
`;

export const CREATE_CUSTOMER_ADDRESS = gql`
  mutation createCustomerAddress($input: CustomerAddressInput!) {
    createCustomerAddress(input: $input) {
      id
      firstname
      lastname
      street
      city
      region {
        region
        region_id
      }
      postcode
      country_code
      telephone
      company
      default_shipping
      default_billing
    }
  }
`;

export const UPDATE_CUSTOMER_ADDRESS = gql`
  mutation updateCustomerAddress($id: Int!, $input: CustomerAddressInput!) {
    updateCustomerAddress(id: $id, input: $input) {
      id
      firstname
      lastname
      street
      city
      region {
        region
        region_id
      }
      postcode
      country_code
      telephone
      company
      default_shipping
      default_billing
    }
  }
`;

export const DELETE_CUSTOMER_ADDRESS = gql`
  mutation deleteCustomerAddress($id: Int!) {
    deleteCustomerAddress(id: $id)
  }
`;

export const REORDER_ITEMS = gql`
  mutation reorderItems($orderNumber: String!) {
    reorderItems(orderNumber: $orderNumber) {
      cart {
        id
        items {
          id
          product {
            name
            sku
          }
          quantity
        }
      }
      userInputErrors {
        message
        path
      }
    }
  }
`;

export const REQUEST_PASSWORD_RESET = gql`
  mutation requestPasswordResetEmail($email: String!) {
    requestPasswordResetEmail(email: $email)
  }
`;

export const RESET_PASSWORD = gql`
  mutation resetPassword($email: String!, $resetPasswordToken: String!, $newPassword: String!) {
    resetPassword(
      email: $email
      resetPasswordToken: $resetPasswordToken
      newPassword: $newPassword
    )
  }
`;

export const REMOVE_PRODUCTS_FROM_WISHLIST = gql`
  mutation removeProductsFromWishlist($wishlistId: ID!, $wishlistItemsIds: [ID!]!) {
    removeProductsFromWishlist(
      wishlistId: $wishlistId
      wishlistItemsIds: $wishlistItemsIds
    ) {
      wishlist {
        id
        items_count
        items_v2(currentPage: 1, pageSize: 100) {
          items {
            id
            quantity
            description
            added_at
            product {
              id
              name
              sku
              url_key
              image {
                url
                label
              }
              price_range {
                minimum_price {
                  regular_price {
                    value
                    currency
                  }
                  final_price {
                    value
                    currency
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const ADD_SIMPLE_PRODUCTS_TO_CART_FROM_WISHLIST = gql`
  mutation addSimpleProductsToCart($cartId: String!, $cartItems: [SimpleProductCartItemInput!]!) {
    addSimpleProductsToCart(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        total_quantity
        items {
          id
          quantity
          product {
            id
            name
            sku
            url_key
            small_image {
              url
              label
            }
            price_range {
              minimum_price {
                regular_price {
                  value
                  currency
                }
                final_price {
                  value
                  currency
                }
              }
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
        }
      }
    }
  }
`;

export const ADD_CONFIGURABLE_PRODUCTS_TO_CART_FROM_WISHLIST = gql`
  mutation addConfigurableProductsToCart($cartId: String!, $cartItems: [ConfigurableProductCartItemInput!]!) {
    addConfigurableProductsToCart(input: {
      cart_id: $cartId
      cart_items: $cartItems
    }) {
      cart {
        id
        total_quantity
        items {
          id
          quantity
          product {
            id
            name
            sku
            url_key
            small_image {
              url
              label
            }
            price_range {
              minimum_price {
                regular_price {
                  value
                  currency
                }
                final_price {
                  value
                  currency
                }
              }
            }
          }
        }
        prices {
          grand_total {
            currency
            value
          }
        }
      }
    }
  }
`;

export const ADD_PRODUCTS_TO_WISHLIST = gql`
  mutation addProductsToWishlist($wishlistId: ID!, $wishlistItems: [WishlistItemInput!]!) {
    addProductsToWishlist(
      wishlistId: $wishlistId
      wishlistItems: $wishlistItems
    ) {
      wishlist {
        id
        items_count
        items_v2(currentPage: 1, pageSize: 100) {
          items {
            id
            quantity
            description
            added_at
            product {
              id
              name
              sku
              url_key
              image {
                url
                label
              }
              price_range {
                minimum_price {
                  regular_price {
                    value
                    currency
                  }
                  final_price {
                    value
                    currency
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const ADD_PRODUCT_TO_WISHLIST = gql`
  mutation addProductToWishlist($wishlistId: ID!, $wishlistItems: [WishlistItemInput!]!) {
    addProductsToWishlist(
      wishlistId: $wishlistId
      wishlistItems: $wishlistItems
    ) {
      wishlist {
        id
        items_count
        items_v2(currentPage: 1, pageSize: 100) {
          items {
            id
            quantity
            product {
              id
              name
              sku
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_WISHLIST_ITEMS = gql`
  mutation updateProductsInWishlist($wishlistId: ID!, $wishlistItems: [WishlistItemUpdateInput!]!) {
    updateProductsInWishlist(
      wishlistId: $wishlistId
      wishlistItems: $wishlistItems
    ) {
      wishlist {
        id
        items_count
        items_v2(currentPage: 1, pageSize: 100) {
          items {
            id
            quantity
            product {
              id
              name
              sku
            }
          }
        }
      }
    }
  }
`;